import React from 'react';
import { css } from '@emotion/react';
import { Stack, Inline, Text, Draft, ToggleInput } from '@resi-media/resi-ui';
import { SafeDataObject } from '@preview/helpers/SafeDataObject';

const QUICK_OFFSETS_MIN = [5, 10, 15, 30, 60] as const;
const DEFAULT_LEAD_TIME_MS = 3_600_000;

type _Props = {
  dataValue: string;
  onChange: (newDataString: string) => void;
};

const ChannelMockFields = ({ dataValue, onChange }: _Props) => {
  const dataWrapper = new SafeDataObject(dataValue);
  const isLive = dataWrapper.get<boolean>('live', false);
  const hasNextEvent = dataWrapper.get<unknown>('nextLive', null) !== null;

  const setNextLiveTime = (iso: string) => {
    onChange(
      dataWrapper
        .setNested('nextLive', 'startTime', iso)
        .setNested('nextLive', 'earliestLiveTimeEstimate', iso)
        .toString()
    );
  };

  return (
    <Stack gap="s">
      <Draft.FormField fieldLabel="Channel Name">
        <Draft.TextInput
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(dataWrapper.set('channelName', e.target.value).toString());
          }}
          value={dataWrapper.get('channelName', '')}
        />
      </Draft.FormField>
      <Draft.FormField fieldLabel="Override Live">
        <ToggleInput
          checked={isLive}
          onChange={(e) => {
            let newData = dataWrapper.set('live', e.target.checked);
            if (e.target.checked && newData.get<unknown>('nextLive', null) === null) {
              newData = newData.set('nextLive', {
                name: '',
                description: '',
                startTime: new Date().toISOString(),
                earliestLiveTimeEstimate: new Date().toISOString(),
              });
            }
            onChange(newData.toString());
          }}
        />
      </Draft.FormField>
      {isLive ? (
        hasNextEvent && (
          <React.Fragment>
            <Draft.FormField fieldLabel="Event Name">
              <Draft.TextInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(dataWrapper.setNested('nextLive', 'name', e.target.value).toString());
                }}
                value={dataWrapper.getNested('nextLive', 'name', '')}
              />
            </Draft.FormField>
            <Draft.FormField fieldLabel="Event Description">
              <Draft.TextInput
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(dataWrapper.setNested('nextLive', 'description', e.target.value).toString());
                }}
                value={dataWrapper.getNested('nextLive', 'description', '')}
              />
            </Draft.FormField>
          </React.Fragment>
        )
      ) : (
        <React.Fragment>
          <Draft.FormField fieldLabel="Has Upcoming Event">
            <ToggleInput
              checked={hasNextEvent}
              onChange={(e) => {
                const defaultTime = new Date(Date.now() + DEFAULT_LEAD_TIME_MS).toISOString();
                onChange(
                  dataWrapper
                    .set(
                      'nextLive',
                      e.target.checked
                        ? {
                            name: '',
                            description: '',
                            startTime: defaultTime,
                            earliestLiveTimeEstimate: defaultTime,
                          }
                        : null
                    )
                    .toString()
                );
              }}
            />
          </Draft.FormField>
          {hasNextEvent && (
            <React.Fragment>
              <Draft.FormField fieldLabel="Event Name">
                <Draft.TextInput
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(dataWrapper.setNested('nextLive', 'name', e.target.value).toString());
                  }}
                  value={dataWrapper.getNested('nextLive', 'name', '')}
                />
              </Draft.FormField>
              <Draft.FormField fieldLabel="Event Description">
                <Draft.TextInput
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(dataWrapper.setNested('nextLive', 'description', e.target.value).toString());
                  }}
                  value={dataWrapper.getNested('nextLive', 'description', '')}
                />
              </Draft.FormField>
              <Draft.FormField fieldLabel="Start Time">
                <Stack gap="xs">
                  <Inline gap="xs">
                    {QUICK_OFFSETS_MIN.map((mins) => (
                      <Draft.Button
                        key={mins}
                        label={`+${mins}m`}
                        onClick={() => {
                          setNextLiveTime(new Date(Date.now() + mins * 60_000).toISOString());
                        }}
                        variant="outlined"
                      >
                        +{mins}m
                      </Draft.Button>
                    ))}
                  </Inline>
                  <input
                    css={css`
                      width: 100%;
                      padding: 6px 8px;
                      border: 1px solid #d9d9d9;
                      border-radius: 4px;
                      font-size: 14px;
                    `}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setNextLiveTime(e.target.value ? new Date(e.target.value).toISOString() : '');
                    }}
                    type="datetime-local"
                    value={(() => {
                      const iso = dataWrapper.getNested<string>('nextLive', 'startTime', '');
                      if (!iso) return '';
                      const d = new Date(iso);
                      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                    })()}
                  />
                  {(() => {
                    const iso = dataWrapper.getNested<string>('nextLive', 'startTime', '');
                    if (!iso) return null;
                    const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
                    const abs = Math.abs(diffSec);
                    const h = Math.floor(abs / 3600);
                    const m = Math.floor((abs % 3600) / 60);
                    const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
                    return (
                      <Text
                        css={css`
                          color: ${diffSec > 0 ? '#52c41a' : '#ff4d4f'};
                          font-size: 12px;
                        `}
                        variant="body4"
                      >
                        {diffSec > 0 ? `in ${label}` : `${label} ago — banner won't show`}
                      </Text>
                    );
                  })()}
                </Stack>
              </Draft.FormField>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Stack>
  );
};

export default ChannelMockFields;
